import { useCallback, useEffect, useState } from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { acquireApiToken } from '@/auth/acquireApiToken';
import type { QrCodeRecord, QrCodeType } from './types';

export type ListStatus = 'active' | 'archived' | 'ended';

interface ListResult {
  data: QrCodeRecord[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useListQrCodes(type: QrCodeType, status: ListStatus): ListResult {
  const isAuthenticated = useIsAuthenticated();
  const { instance, accounts } = useMsal();

  const [data, setData] = useState<QrCodeRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!isAuthenticated || accounts.length === 0) return;
    const apiUrl = process.env.REACT_APP_API_URL;
    if (!apiUrl) {
      setError('REACT_APP_API_URL is not set');
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        const tokenResponse = { accessToken: await acquireApiToken(instance, accounts[0]) };
        const headers = {
          Authorization: `Bearer ${tokenResponse.accessToken}`,
          'Content-Type': 'application/json',
        };
        const params = new URLSearchParams({ type, status });
        const res = await fetch(`${apiUrl}/admin/qr-codes?${params.toString()}`, { headers });
        const json = await res.json();
        if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
        if (!cancelled) {
          setData((json.data ?? []) as QrCodeRecord[]);
          setError(null);
        }
      } catch (err: unknown) {
        if (!cancelled) {
          const msg = err instanceof Error ? err.message : 'Failed to load QR codes';
          setError(msg);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, instance, accounts, type, status, tick]);

  return { data, loading, error, refetch };
}
